import React, {
  useState,
  useEffect,
  useContext,
  Suspense,
  useCallback,
} from "react";
import { CircularProgress, CssBaseline } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { useAuth } from "react-oidc-context";
import { isLoggingOut } from "./config/authConfig";
import { Box } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "./components/ErrorFallback";
import UserContext, { UserProvider } from "./contexts/UserContext";
import { ThemeProvider } from "@mui/material/styles";
import SessionChecker from "./components/sessionChecker/SessionChecker";
import { initializeFontAwesome } from "./utils/fontAwesome";
import { useNotificationHandling } from "./utils/notificationHandle";
import { config } from "./config/Config";
import axios from "axios";

const lazyWithRetry = (componentImport) => {
  return React.lazy(() =>
    componentImport().catch((error) => {
      const hasReloaded = sessionStorage.getItem("chunk_reload");
      if (!hasReloaded) {
        sessionStorage.setItem("chunk_reload", "true");
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem("chunk_reload");
      throw error;
    })
  );
};

const AppLayout = lazyWithRetry(() => import("./components/appLayout/AppLayout"));
const AppRoutes = lazyWithRetry(() => import("./AppRoutes"));

// Initialize FontAwesome
initializeFontAwesome();

const LoadingSpinner = React.memo(() => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    height="100vh"
    width="100%"
  >
    <CircularProgress />
  </Box>
));

function AuthenticatedApp() {
  const [theme, colorMode] = useMode();
  const { loading: userLoading } = useContext(UserContext);
  const { handleErrorNotification } = useNotificationHandling();
  const [healthCheckLoading, setHealthCheckLoading] = useState(true);
  const [health, setHealth] = useState({
    enabled: false,
    message: "",
  });

  const checkHealth = useCallback(async () => {
    try {
      const response = await axios.get(config.healthCheckUrl);
      const data = response.data;
      setHealth({
        enabled: data.status === "up" ? true : false,
        message: data.message,
      });
    } catch (error) {
      handleErrorNotification(error);
      setHealth({
        enabled: false,
        message: "Error checking health",
      });
    } finally {
      setHealthCheckLoading(false);
    }
  }, [handleErrorNotification]);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  if (userLoading || healthCheckLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          {!health?.enabled ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh",
              }}
            >
              <h1>{health.message}</h1>
            </div>
          ) : (
            <>
              <CssBaseline />
              <SessionChecker />
              <Suspense fallback={<LoadingSpinner />}>
                <div className="app">
                  <AppLayout>
                    <AppRoutes />
                  </AppLayout>
                </div>
              </Suspense>
            </>
          )}
        </ThemeProvider>
      </ColorModeContext.Provider>
    </ErrorBoundary>
  );
}

function App() {
  const auth = useAuth();

  useEffect(() => {
    // Don't redirect during logout — cognitoLogout() sets this flag before
    // removeUser() so that the signinRedirect below doesn't race and override
    // the window.location.href to Cognito's /logout endpoint.
    if (isLoggingOut()) return;

    const hasCodeInUrl = window.location.search.includes("code=");
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.error &&
      !hasCodeInUrl
    ) {
      auth.signinRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error]);

  if (auth.isLoading) {
    return <LoadingSpinner />;
  }

  if (auth.error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
        gap={2}
      >
        <h2>Authentication Error</h2>
        <p>{auth.error.message}</p>
        <button
          onClick={() => auth.signinRedirect()}
          style={{ padding: "8px 16px", cursor: "pointer" }}
        >
          Try Again
        </button>
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return <LoadingSpinner />;
  }

  return (
    <UserProvider>
      <AuthenticatedApp />
    </UserProvider>
  );
}

export default App;
